import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Alert, AlertDescription } from '@quebon/ui/components/alert'
import { Button } from '@quebon/ui/components/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@quebon/ui/components/dialog'
import { lcCoachingApi } from '@/api/lcCoaching'
import { deleteCoachingVideo, listCoachingVideos, retryCoachingVideo } from '@/api/lcCoachingVideos'
import { ExplainVideoError, getExplainVideoCapability, type ExplainVideoView } from '@/api/lcExplainVideos'
import { LcEvidenceVideoPlayer } from '@/components/common/LcEvidenceVideoPlayer'
import { ExplainVideoPanel } from '@/components/photo/ExplainVideoPanel'
import { growthAlertKeys } from '@/hooks/coaching/useGrowthAlertPreview'
import { getApiErrorCode, getApiErrorMessage } from '@/utils/apiError'
import type { LessonPhotoMedia } from '../lesson-photos/LessonPhotoPanel'

interface Props {
  learnerId: number
  lessonId: number
  configComponentId: string
  componentProgressId: number
  initialVideos: ExplainVideoView[]
  canEdit: boolean
  locked: boolean
  lock: () => boolean
  unlock: () => void
  children: (media: LessonPhotoMedia | undefined) => ReactNode
}

/** Shared by today's mobile lesson and the standalone grading workspace. */
export function HandheldBuildupVideos({ learnerId, lessonId, configComponentId, componentProgressId, initialVideos, canEdit, locked, lock, unlock, children }: Props) {
  const { t } = useTranslation(['coaching', 'common', 'video'])
  const qc = useQueryClient()
  const key = ['coaching', 'learner', learnerId, 'handheld-buildup-videos', lessonId, configComponentId, componentProgressId] as const
  const [deleteTarget, setDeleteTarget] = useState<ExplainVideoView | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [uploadPending, setUploadPending] = useState(false)
  const changing = useRef(false)
  const uploadLease = useRef(false)
  const pendingRef = useRef(false)
  const lockRef = useRef(lock)
  const unlockRef = useRef(unlock)
  lockRef.current = lock
  unlockRef.current = unlock
  const handlePendingChange = useCallback((pending: boolean) => {
    if (pendingRef.current === pending) return !pending || uploadLease.current
    if (pending && !uploadLease.current) {
      if (!lockRef.current()) return false
      uploadLease.current = true
    }
    pendingRef.current = pending
    if (!pending && uploadLease.current) {
      uploadLease.current = false
      unlockRef.current()
    }
    setUploadPending(pending)
    return true
  }, [])
  useEffect(() => () => {
    if (uploadLease.current) {
      uploadLease.current = false
      unlockRef.current()
    }
  }, [])
  const videosQuery = useQuery({
    queryKey: key,
    queryFn: () => listCoachingVideos(learnerId, componentProgressId),
    placeholderData: initialVideos,
    retry: false,
    refetchOnWindowFocus: 'always',
    refetchInterval: query => document.visibilityState !== 'hidden'
      && query.state.data?.some(video => ['RESERVED', 'UPLOADING', 'PROCESSING'].includes(video.status)) ? 5000 : false,
  })
  // Read-only history may view server-authorized videos, but never issues an upload token.
  const upload = useQuery({
    queryKey: [...key, 'upload-capability'],
    queryFn: async () => {
      const token = await lcCoachingApi.issueBuildupExplainPhotoToken(learnerId, componentProgressId)
      return { ...token, capability: await getExplainVideoCapability(token.token) }
    },
    enabled: canEdit && !locked,
    retry: false,
    staleTime: 30_000,
    refetchOnWindowFocus: 'always',
  })
  const handleVideosChange = useCallback(() => {
    const queryKey = ['coaching', 'learner', learnerId, 'handheld-buildup-videos', lessonId, configComponentId, componentProgressId] as const
    // The composer refreshes its own capability. Refetching this query would issue a
    // new QR token while a recording or resumable upload still owns the old token.
    void qc.invalidateQueries({ queryKey, exact: true })
    void qc.invalidateQueries({ queryKey: growthAlertKeys.learnerPreviews(learnerId) })
  }, [componentProgressId, configComponentId, learnerId, lessonId, qc])
  const videos = videosQuery.data ?? []
  const uploadSession = upload.data
  const enabled = upload.data?.capability.enabled === true
  const knownDisabled = upload.data?.capability.reason === 'VIDEO_LEARNING_CENTER_ONLY' || getApiErrorCode(videosQuery.error) === 'VIDEO_LEARNING_CENTER_ONLY'
  const listAuthorized = videosQuery.isSuccess && !videosQuery.isPlaceholderData
  const visible = !knownDisabled && (enabled || (listAuthorized && videos.length > 0))
  const canDelete = canEdit && listAuthorized && !knownDisabled
  const maxVideos = upload.data?.capability.maxVideos ?? 3
  const remaining = Math.max(0, maxVideos - Math.max(videos.length, upload.data?.capability.currentCount ?? 0))
  const message = (error: unknown) => error instanceof ExplainVideoError
    ? t(`apiError.${error.code}`, { ns: 'common', defaultValue: t('requestFailed', { ns: 'video' }) })
    : getApiErrorMessage(error, t)
  const mutation = useMutation({
    mutationFn: async ({ video, action }: { video: ExplainVideoView; action: 'retry' | 'delete' }) => {
      if (action === 'retry') return retryCoachingVideo(learnerId, componentProgressId, video.id)
      await deleteCoachingVideo(learnerId, componentProgressId, video.id)
      return null
    },
  })
  const changeVideo = async (video: ExplainVideoView, action: 'retry' | 'delete') => {
    if ((action === 'retry' ? !canEdit || !enabled : !canDelete) || locked || changing.current || !lock()) return
    changing.current = true
    setActionError(null)
    try {
      const updated = await mutation.mutateAsync({ video, action })
      qc.setQueryData<ExplainVideoView[]>(key, current => updated
        ? (current ?? []).map(item => item.id === updated.id ? updated : item)
        : (current ?? []).filter(item => item.id !== video.id))
      setDeleteTarget(null)
      const next = await videosQuery.refetch({ throwOnError: true })
      qc.setQueryData<typeof upload.data>([...key, 'upload-capability'], current => current && next.data
        ? { ...current, capability: { ...current.capability, currentCount: next.data.length } } : current)
      void qc.invalidateQueries({ queryKey: growthAlertKeys.learnerPreviews(learnerId) })
    } catch (error) { setActionError(message(error)) }
    finally { changing.current = false; unlock() }
  }
  const refresh = async () => {
    setActionError(null)
    try {
      await Promise.all([
        videosQuery.refetch({ throwOnError: true }),
        ...(canEdit && !locked ? [upload.refetch({ throwOnError: true })] : []),
      ])
    } catch (error) { setActionError(message(error)) }
  }
  const error = actionError ?? (videosQuery.error ? message(videosQuery.error) : upload.error ? message(upload.error) : null)
  const busy = mutation.isPending || locked
  const uploadBlocked = (locked && !uploadLease.current) || mutation.isPending || upload.isFetching

  const media: LessonPhotoMedia | undefined = visible ? {
    count: videos.length,
    maxCount: maxVideos,
    pending: uploadPending,
    refreshing: videosQuery.isFetching || upload.isFetching,
    onRefresh: refresh,
    composer: canEdit && enabled && uploadSession ? active => <ExplainVideoPanel
      token={uploadSession.token}
      active={active}
      initialCapability={uploadSession.capability}
      variant="embedded"
      disabled={uploadBlocked}
      onPendingChange={handlePendingChange}
      onVideosChange={handleVideosChange}
    /> : undefined,
    content: <>
      {canEdit && enabled && remaining === 0 ? <p className="text-sm text-muted-foreground">{t('limitReached', { ns: 'video', count: maxVideos })}</p> : null}
      {error ? <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert> : null}
      {videos.length > 0 ? <div className="grid min-w-0 gap-3 sm:grid-cols-2">
        {videos.map((video, index) => <div key={video.id} className="flex min-w-0 flex-col gap-2 rounded-lg border border-border bg-card p-3">
          <p className="text-sm font-medium">{t('buildup.video.label', { index: index + 1 })}</p>
          {video.status === 'READY' && video.playbackUrl ? <LcEvidenceVideoPlayer
            url={video.playbackUrl}
            label={t('buildup.video.label', { index: index + 1 })}
            onRefreshUrl={async () => (await videosQuery.refetch({ throwOnError: true })).data?.find(item => item.id === video.id)?.playbackUrl ?? undefined}
          /> : <p role="status" className={video.status === 'FAILED' ? 'text-sm text-destructive' : 'text-sm text-muted-foreground'}>
            {t(video.status === 'READY' ? 'growthAlert.videoUnavailable' : `buildup.video.status.${video.status}`)}
          </p>}
          {canDelete ? <div className="flex flex-wrap gap-2">
            {enabled && video.status === 'FAILED' ? <Button type="button" variant="outline" className="min-h-11" disabled={busy} onClick={() => void changeVideo(video, 'retry')}>{t('buildup.video.retry')}</Button> : null}
            <Button type="button" variant="destructive" className="min-h-11" disabled={busy} onClick={() => setDeleteTarget(video)}>{t('buildup.video.delete')}</Button>
          </div> : null}
        </div>)}
      </div> : null}
    </>,
  } : undefined

  return <>
    {children(media)}
    <Dialog open={visible && deleteTarget != null} onOpenChange={open => { if (!open && !mutation.isPending) setDeleteTarget(null) }}>
      <DialogContent>
        <DialogHeader><DialogTitle>{t('buildup.video.delete')}</DialogTitle><DialogDescription>{t('buildup.video.deleteConfirm')}</DialogDescription></DialogHeader>
        {actionError ? <Alert variant="destructive"><AlertDescription>{actionError}</AlertDescription></Alert> : null}
        <DialogFooter>
          <Button type="button" variant="outline" className="min-h-11" disabled={mutation.isPending} onClick={() => setDeleteTarget(null)}>{t('button.cancel', { ns: 'common' })}</Button>
          <Button type="button" variant="destructive" className="min-h-11" disabled={busy || !canDelete} onClick={() => { if (deleteTarget) void changeVideo(deleteTarget, 'delete') }}>{t('buildup.video.delete')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </>
}
